import type {ChatState} from '@microsoft/agent-host-protocol'
import {chatChannelFor, sessionChannel} from '@github-ui/ahp-relay/relay-connection-runtime'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {fetchAhpSessionEvents, generateAhpEventsQueryKey, type AhpEventsResult} from '../utils/fetch-ahp-session-events'
import {rebuildAhpSessionBundleFromEvents, type AhpSessionBundle} from '../utils/replay-ahp-events'
import type {Session} from '../types/session'
import {isConversationRefused} from '../utils/conversation-access'
import {useTaskHistoryRequired} from '../contexts/new-task-context'

export interface UseAhpSessionEventsOptions {
  session: Session | null | undefined
  /** When false the query is inert. Defaults to true. */
  enabled?: boolean
  refetchInterval?: number | false
}

export interface UseAhpSessionEventsResult {
  sessionBundle: AhpSessionBundle | undefined
  chatState: ChatState | undefined
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Fetches a session's persisted AHP events and rebuilds a `ChatState`, for AHP
 * sessions the live relay can't replay. Callers MUST gate on `isAhpSession`.
 */
export function useAhpSessionEvents({
  session,
  enabled = true,
  refetchInterval = false,
}: UseAhpSessionEventsOptions): UseAhpSessionEventsResult {
  const sessionId = session?.id
  const historyRequired = useTaskHistoryRequired(session?.task_id)
  const queryEnabled = enabled && !!sessionId && historyRequired

  const query = useCAPIQuery<AhpEventsResult, AhpSessionBundle | undefined>({
    queryKey: generateAhpEventsQueryKey(sessionId),
    queryFn: async () => {
      if (!sessionId) throw new Error('No session ID provided')
      return fetchAhpSessionEvents(sessionId)
    },
    select: (data: AhpEventsResult) => {
      const resource = sessionChannel(sessionId ?? '')
      return rebuildAhpSessionBundleFromEvents(data.events, {
        sessionChannel: resource,
        chatChannel: chatChannelFor(resource),
        title: session?.name ?? '',
        modifiedAt: session?.updated_at ?? session?.last_updated_at ?? '',
      })
    },
    enabled: queryEnabled,
    // Stop polling a refused conversation: it stays refused until access changes.
    refetchInterval: current => (isConversationRefused(current.state.error) ? false : refetchInterval),
  })

  return {
    sessionBundle: query.data,
    chatState: query.data?.defaultChatState,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
